/**
 * Utility functions for language detection and handling
 */
import React from 'react';

/**
 * Detects if a text contains Hebrew characters and returns the language direction
 * @param {string} text - The text to analyze
 * @returns {string} - 'rtl' for Hebrew content, 'ltr' for others
 */
export const detectLanguageDirection = (text) => {
  if (!text || typeof text !== 'string') return 'ltr';
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text) ? 'rtl' : 'ltr';
};

/**
 * Detects if a text contains Hebrew characters and returns the language code
 * @param {string} text - The text to analyze
 * @returns {string} - 'he' for Hebrew content, 'en' for others
 */
export const detectLanguage = (text) => {
  if (!text || typeof text !== 'string') return 'en';
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text) ? 'he' : 'en';
};

/**
 * Splits text with mixed language content and wraps English parts in appropriate spans
 * @param {string} text - The mixed content text
 * @param {boolean} isHeading - Whether this is a heading element
 * @returns {(string|JSX.Element)[]} - Array of text segments and wrapped spans
 */
export const splitTextAndWrap = (text, isHeading = false) => {
  if (typeof text !== 'string') return text;
  
  // If it's an English heading, return as is
  if (isHeading && detectLanguage(text) === 'en') {
    return text;
  }

  // Only for Hebrew or mixed text
  const regex = /(\([^)]+\)|[a-zA-Z-]+(?:\s+[a-zA-Z-]+)*)/g;
  let lastIndex = 0;
  const parts = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(match[0]);
    lastIndex = regex.lastIndex;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.map((part, index) => {
    const isEnglish = /^[a-zA-Z-\s()]+$/.test(part);
    return isEnglish ? (
      <span
        key={index}
        style={{
          display: 'inline-block',
          direction: 'ltr',
          unicodeBidi: 'embed'
        }}
      >
        {part}
      </span>
    ) : part;
  });
};

/**
 * Wraps content with appropriate language styling based on parent language
 * @param {string|React.ReactNode} children - The content to wrap
 * @param {string} parentLang - Parent language ('en' or 'he')
 * @param {boolean} isHeading - Whether this is a heading element
 * @returns {JSX.Element} - Wrapped content with appropriate direction
 */
export const wrapWithLanguage = (children, parentLang, isHeading = false) => {
  if (typeof children !== 'string') return children;
  
  // Special handling for English headings
  if (isHeading && parentLang === 'en') {
    return (
      <span style={{ 
        display: 'block',
        direction: 'ltr',
        textAlign: 'left',
        width: '100%'
      }}>
        {children}
      </span>
    );
  }

  // Handle regular text
  return (
    <span style={{ 
      display: 'block',
      direction: parentLang === 'en' ? 'ltr' : 'rtl',
      textAlign: parentLang === 'en' ? 'left' : 'right'
    }}>
      {splitTextAndWrap(children, isHeading)}
    </span>
  );
};
